import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Download, FilePlus2, LayoutGrid, List, Search } from "lucide-react";
import api, { apiError, downloadFile } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";
import { STATUSES, fmtDateTime, useLabels } from "../lib/constants";
import { CompanyBadge, PriorityBadge, StatusBadge, TypeBadge } from "../components/Badges";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function Cases() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const L = useLabels();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [status, setStatus] = useState("all");
  const [company, setCompany] = useState("all");
  const [priority, setPriority] = useState(searchParams.get("priority") || "all");
  const [exporting, setExporting] = useState(false);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUSES.map((s) => [s, []]));
    cases.forEach((c) => {
      (g[c.status] || (g[c.status] = [])).push(c);
    });
    return g;
  }, [cases]);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (qDebounced) p.set("q", qDebounced);
    if (status !== "all") p.set("status", status);
    if (company !== "all") p.set("company", company);
    if (priority !== "all") p.set("priority", priority);
    return p.toString();
  }, [qDebounced, status, company, priority]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/cases?${params}`);
      setCases(r.data);
    } catch (e) {
      toast.error(apiError(e, t("cases.loadFail")));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const canExport = ["lawyer", "admin", "super_admin"].includes(user.role);

  const doExport = async () => {
    setExporting(true);
    try {
      await downloadFile(`/export/cases?${params}`, "delta_cases_export.xlsx");
      toast.success(t("cases.exported"));
    } catch (e) {
      toast.error(apiError(e, t("cases.exportFail")));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="cases-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("cases.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {t(user.role === "requester" ? "cases.subRequester" : "cases.subStaff")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <Button variant="outline" data-testid="export-excel-button" onClick={doExport} disabled={exporting}
              className="border-delta-limeBorder text-delta-navy hover:bg-delta-limeLight">
              <Download className="me-2 h-4 w-4" /> {exporting ? t("cases.exporting") : t("cases.export")}
            </Button>
          )}
          <Button data-testid="create-case-button" onClick={() => navigate("/cases/new")} className="bg-delta-navy hover:bg-delta-navyDark">
            <FilePlus2 className="me-2 h-4 w-4" /> {t("cases.new")}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-white p-3">
        <div className="relative min-w-52 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="cases-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("cases.search")}
            className="pl-9"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="status-filter" className="w-44"><SelectValue placeholder={t("cases.colStatus")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("cases.allStatuses")}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{L.status[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={company} onValueChange={setCompany}>
          <SelectTrigger data-testid="company-category-filter" className="w-44"><SelectValue placeholder={t("cases.colCompany")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("cases.allCompanies")}</SelectItem>
            {Object.entries(L.company).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger data-testid="priority-filter" className="w-36"><SelectValue placeholder={t("cases.colPriority")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("cases.allPriorities")}</SelectItem>
            {Object.entries(L.priority).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ms-auto flex rounded-lg border p-0.5">
          <button data-testid="view-table-button" onClick={() => setView("table")}
            className={`rounded-md p-1.5 transition-colors ${view === "table" ? "bg-delta-navy text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            <List className="h-4 w-4" />
          </button>
          <button data-testid="view-kanban-button" onClick={() => setView("kanban")}
            className={`rounded-md p-1.5 transition-colors ${view === "kanban" ? "bg-delta-navy text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            <LayoutGrid className="h-4 w-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center" data-testid="cases-loading">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-delta-navy border-t-transparent" />
        </div>
      ) : cases.length === 0 ? (
        <div className="rounded-xl border bg-white py-16 text-center" data-testid="cases-empty">
          <p className="text-sm text-slate-500">{t("cases.empty")}</p>
          <Button data-testid="cases-empty-new-button" onClick={() => navigate("/cases/new")} className="mt-4 bg-delta-navy hover:bg-delta-navyDark">
            <FilePlus2 className="me-2 h-4 w-4" /> {t("cases.emptyCta")}
          </Button>
        </div>
      ) : view === "table" ? (
        <div className="overflow-x-auto rounded-xl border bg-white" data-testid="cases-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50 text-start text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-semibold">{t("cases.colNumber")}</th>
                <th className="px-4 py-3 font-semibold">{t("cases.colTitle")}</th>
                <th className="px-4 py-3 font-semibold">{t("cases.colType")}</th>
                <th className="px-4 py-3 font-semibold">{t("cases.colCompany")}</th>
                <th className="px-4 py-3 font-semibold">{t("cases.colPriority")}</th>
                <th className="px-4 py-3 font-semibold">{t("cases.colStatus")}</th>
                <th className="px-4 py-3 font-semibold">{t("cases.colAssigned")}</th>
                <th className="px-4 py-3 font-semibold">{t("cases.colUpdated")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {cases.map((c) => (
                <tr key={c.id} data-testid={`case-row-${c.case_number}`} onClick={() => navigate(`/cases/${c.id}`)}
                  className="cursor-pointer transition-colors hover:bg-delta-limeLight/40">
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs font-semibold text-delta-navy">{c.case_number}</td>
                  <td className="max-w-64 truncate px-4 py-3 font-medium text-slate-800">{c.title}</td>
                  <td className="px-4 py-3"><TypeBadge type={c.case_type} /></td>
                  <td className="px-4 py-3"><CompanyBadge company={c.company} /></td>
                  <td className="px-4 py-3"><PriorityBadge priority={c.priority} /></td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">{c.assigned_lawyer?.name || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-400">{fmtDateTime(c.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="kanban-scroll flex gap-3 overflow-x-auto pb-3" data-testid="cases-kanban">
          {STATUSES.map((s) => (
            <div key={s} className="w-64 shrink-0 rounded-xl border bg-white">
              <div className="border-b px-3 py-2.5">
                <StatusBadge status={s} />
                <span className="ms-2 text-xs text-slate-400">{grouped[s].length}</span>
              </div>
              <div className="max-h-[28rem] space-y-2 overflow-y-auto p-2">
                {grouped[s].map((c) => (
                  <button key={c.id} data-testid={`kanban-card-${c.case_number}`} onClick={() => navigate(`/cases/${c.id}`)}
                    className="w-full rounded-lg border p-3 text-start transition-all hover:border-delta-limeBorder hover:shadow-sm">
                    <p className="font-mono text-[11px] font-semibold text-delta-navy">{c.case_number}</p>
                    <p className="mt-0.5 line-clamp-2 text-sm font-medium text-slate-800">{c.title}</p>
                    <div className="mt-2 flex items-center gap-1.5">
                      <PriorityBadge priority={c.priority} />
                      <CompanyBadge company={c.company} />
                    </div>
                  </button>
                ))}
                {grouped[s].length === 0 && (
                  <p className="py-6 text-center text-xs text-slate-300">{t("common.empty")}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
